import { Box2, ColorRepresentation, Vector2, Vector2Like, Vector3, Vector3Like, Vector3Tuple } from 'three'
import Util from '../lib/util'
import Units from '../lib/units'
import { sortBy } from 'lodash-es'
import { createContext, useContext } from 'react'
import { DEFAULT_WALL_SIZES } from '~/config/wallSizes'
import { guid } from '~/lib/utils'

export type MountingStructureLineProps = {
  model: [Vector3, string]
  next: [Vector3, string] | undefined
  prev: [Vector3, string] | undefined
  name: string
  color: ColorRepresentation
  orientation: Vector3
  type: 'beam' | 'column'
  roofSectionId: string
  z: number
  perimeterPoints: Vector3Tuple[]
  selected: boolean
  setSelected: React.Dispatch<React.SetStateAction<[string, string] | null>>
}

export const MountingStructureData = createContext<MountingStructureLineProps | null>(null)
export function useMountingStructureData() {
  const data = useContext(MountingStructureData)
  if (data === null) {
    throw new Error('useMountingStructureData must be used within a MountingStructureData.Provider')
  }
  return data
}

export const RoofSectionOrientation = createContext<'horizontal' | 'vertical' | null>(null)
export function useRoofSectionOrientation() {
  const data = useContext(RoofSectionOrientation)
  if (data === null) {
    throw new Error('useRoofSectionOrientation must be used within a RoofSectionOrientation.Provider')
  }
  return data
}

const Z_AXIS = new Vector3(0, 0, 1)
export function getOrthoOrientation(beamOrientation: Vector3) {
  const orientation = new Vector3(Math.abs(beamOrientation.x), Math.abs(beamOrientation.y), beamOrientation.z)
  return orientation.clone().applyAxisAngle(Z_AXIS, -Math.PI / 2)
}
export function getBeamOrientation(beamRotation: number) {
  return new Vector3(0, -1, 0).applyAxisAngle(Z_AXIS, beamRotation)
}

function getOrientationAxis(orientation: Vector3): 'x' | 'y' | 'z' {
  const keys = ['x', 'y', 'z'] as const
  return keys.find(key => {
    return Number(orientation[key].toFixed(6))
  })!
}

export type AugmentedModel = { position: Vector3Like; point1: Vector2; point2: Vector2; id: string }
export function getLineSegmentForPoint(
  pos: [Vector2, string],
  sectionPoints: Vector3Tuple[],
  orientation: Vector3,
  useFurthest: boolean
): { point1: Vector2; point2: Vector2; id: string; position: Vector2 } | undefined
export function getLineSegmentForPoint(
  pos: [Vector3, string],
  sectionPoints: Vector3Tuple[],
  orientation: Vector3,
  useFurthest: boolean
): { point1: Vector2; point2: Vector2; id: string; position: Vector3 } | undefined
export function getLineSegmentForPoint(
  pos: [Vector2 | Vector3, string],
  sectionPoints: Vector3Tuple[],
  orientation: Vector3,
  useFurthest: boolean
): { point1: Vector2; point2: Vector2; id: string; position: Vector2 | Vector3 } | undefined
export function getLineSegmentForPoint(
  pos: [Vector2 | Vector3, string],
  sectionPoints: Vector3Tuple[],
  orientation: Vector3,
  useFurthest: boolean
) {
  const orientationKey = getOrientationAxis(orientation)
  const perimeterPoints = Util.toVec3Array(sectionPoints)

  const segPoint1 = pos[0].clone().addScaledVector(orientation, -Units.feetToNative(100000))
  const segPoint2 = pos[0].clone().addScaledVector(orientation, Units.feetToNative(100000))
  let intersectionPoints: Vector2[] = []

  for (let i = 0; i < perimeterPoints.length - 1; i++) {
    const wallSegPoint1 = perimeterPoints[i]
    const wallSegPoint2 = perimeterPoints[i + 1]
    const intersectionPoint = Util.lineSegmentsIntersectionPoint(wallSegPoint1, wallSegPoint2, segPoint1, segPoint2)
    if (intersectionPoint) {
      intersectionPoints.push(new Vector2(intersectionPoint.x, intersectionPoint.y))
    }
  }

  // May happen with non-convex polygons or from imprecision in the
  // intersection calculation.
  if (intersectionPoints.length > 2) {
    if (useFurthest) {
      intersectionPoints = Util.getFurthestTwoPoints(intersectionPoints)
    } else {
      const position = new Vector2(pos[0].x, pos[0].y)
      const positionValue = Number(position[orientationKey as 'x' | 'y'].toPrecision(6))
      intersectionPoints = intersectionPoints.filter(ip => {
        return Number(ip[orientationKey as 'x' | 'y'].toPrecision(6)) > positionValue
      })
      if (intersectionPoints.length) {
        intersectionPoints = [position, Util.getClosestPoint(position, intersectionPoints)]
      }
    }
  }

  // If the number of intersectionPoints isn't two, the line was either
  // tangent to the polygon or does not overlap it at all.
  if (intersectionPoints.length === 2) {
    intersectionPoints = sortBy(intersectionPoints, orientationKey)
    return {
      point1: intersectionPoints[0],
      point2: intersectionPoints[1],
      id: pos[1],
      position: pos[0],
    }
  } else {
    return undefined
  }
}

export function inchesToNative2(it: Vector2Like): Vector2 {
  return new Vector2(Units.inchesToNative(it.x), Units.inchesToNative(it.y))
}
export function become3(it: Vector2Like, z: number) {
  return new Vector3(it.x, it.y, z)
}

export function average(a: Vector3, b: Vector3): Vector3
export function average(a: Vector2, b: Vector2): Vector2
export function average(a: Vector2 | Vector3, b: Vector2 | Vector3): Vector2 | Vector3 {
  return (a as Vector2)
    .clone()
    .add(b as Vector2)
    .multiplyScalar(0.5)
}

export function getMountingStructurePositions(
  sectionPerimeterPoints: Vector3Tuple[],
  orientation: Vector3,
  spacing: number
) {
  const boundingBox = new Box2().setFromPoints(sectionPerimeterPoints.map(([x, y]) => new Vector2(x, y)))
  const isVertical = Math.abs(orientation.round().y) === 1
  const orientationAxis = isVertical ? 'x' : 'y'
  const boundsDistance = boundingBox.max[orientationAxis] - boundingBox.min[orientationAxis]
  const positionCount = Math.ceil(boundsDistance / (spacing || 1))
  const wallOffset = DEFAULT_WALL_SIZES.EXTERIOR_WALLS.thickness / 2
  const startingOffset =
    sectionPerimeterPoints.reduce((min, curr) => Math.min(min, curr[isVertical ? 0 : 1]), Infinity) + wallOffset
  const positions = []
  for (let i = 0; i < positionCount; i++) {
    const axisPosition = i * spacing + startingOffset
    const min = boundingBox.min[orientationAxis]
    const a = new Vector2(...(isVertical ? [axisPosition, min] : [min, axisPosition]))
    const max = boundingBox.max[orientationAxis]
    const b = new Vector2(...(isVertical ? [axisPosition, max] : [max, axisPosition]))
    const c = new Vector2()
    const d = new Vector2()
    const intersections: Vector2[] = []
    for (let index = 0; index < sectionPerimeterPoints.length; index++) {
      if (index === 0) continue
      const currentPoint = sectionPerimeterPoints[index]
      const prevPoint = sectionPerimeterPoints[index - 1]
      c.set(currentPoint[0], currentPoint[1])
      d.set(prevPoint[0], prevPoint[1])
      const numerator = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
      const denonminator = (d.x - c.x) * (b.y - a.y) - (d.y - c.y) * (b.x - a.x)
      const isParallel = denonminator === 0 && numerator !== 0
      const isColinear = denonminator === 0 && numerator === 0
      if (isParallel || isColinear) continue
      const alpha = numerator / denonminator
      const hasIntersection = alpha < 1 && alpha > 0
      if (!hasIntersection) continue
      intersections.push(c.clone().lerp(d, alpha))
    }
    intersections.sort((a, b) => a[orientationAxis === 'x' ? 'y' : 'x'] - b[orientationAxis === 'x' ? 'y' : 'x'])
    for (let j = 0; j < intersections.length; j++) {
      if (j % 2 !== 0) continue
      const { x, y } = intersections[j]
      positions.push({ id: guid(), position: { x, y, z: 0 } })
    }
  }
  return positions
}
